import React from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline' | 'segmented';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-sm',
    lg: 'px-6 py-4 text-base',
  };

  const getTabClasses = (tab: TabItem, isActive: boolean) => {
    const baseClasses = `cursor-pointer select-none font-medium transition-all duration-200 ${sizeClasses[size]} flex items-center justify-center relative`;
    
    if (variant === 'pills') {
      return clsx(
        baseClasses,
        'rounded-xl',
        isActive
          ? 'bg-primary text-primary-foreground shadow-md transform scale-[1.02]'
          : 'text-muted-foreground hover:text-primary hover:bg-primary/10 hover:shadow-sm',
        tab.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      );
    }
    
    if (variant === 'underline') {
      return clsx(
        baseClasses,
        'border-b-2 rounded-none pb-3',
        isActive
          ? 'border-primary text-primary bg-primary/5'
          : 'border-transparent text-muted-foreground hover:text-primary hover:border-primary/30',
        tab.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      );
    }
    
    if (variant === 'segmented') {
      return clsx(
        baseClasses,
        'first:rounded-l-lg last:rounded-r-lg',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm z-10'
          : 'bg-card text-muted-foreground hover:text-foreground hover:bg-accent/50',
        tab.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      );
    }
    
    // Default variant
    return clsx(
      baseClasses,
      'rounded-lg',
      isActive
        ? 'bg-accent text-accent-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
      tab.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
    );
  };

  const containerClasses = clsx(
    'flex',
    variant === 'pills' && 'bg-muted/30 backdrop-blur-sm border border-border rounded-2xl p-1 gap-1',
    variant === 'underline' && 'border-b border-border gap-8',
    variant === 'segmented' && 'border border-border rounded-lg overflow-hidden w-fit',
    variant === 'default' && 'gap-2',
    className
  );

  return (
    <nav className={containerClasses} role="tablist">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        
        return (
          <div
            key={tab.id}
            onClick={() => !tab.disabled && onTabChange(tab.id)}
            className={getTabClasses(tab, isActive)}
            role="tab"
            tabIndex={tab.disabled ? -1 : 0}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !tab.disabled) {
                e.preventDefault();
                onTabChange(tab.id);
              }
            }}
          >
            {Icon && (
              <Icon 
                className={clsx(
                  'h-5 w-5 transition-colors duration-200',
                  tab.label && 'mr-2'
                )} 
              />
            )}
            <span className="whitespace-nowrap">
              {tab.label}
            </span>
            {tab.count !== undefined && (
              <span className={clsx(
                "ml-2 px-2 py-0.5 text-xs font-medium rounded-full",
                isActive
                  ? "bg-primary-foreground/20 text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              )}>
                {tab.count}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default Tabs;